// Messages → Unmatched texts (`/app/messages/unmatched-sms`).
//
// Inbound SMS to a host's Twilio number from a phone that matches no member
// land in `unmatched_inbound_sms` (server/routes/public.ts). The Messages
// inbox banner has linked here since Task #893, but no page ever existed.
// This is the triage surface the server routes were built for:
//   pending  → Assign to a member (optionally updating their phone) / Ignore
//   assigned → open the member's conversation / Reassign
//   ignored  → Reopen
// Routes: GET /api/app/unmatched-sms?status=…, POST …/:id/assign|ignore|
// reassign|reopen (server/routes/devices-messaging.ts).

import { useMemo, useState } from "react";
import { Link } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowRight, Loader2, MessageSquareWarning, RotateCcw, UserPlus, XCircle } from "lucide-react";
import { format } from "date-fns";
import { usePageTitle } from "@/hooks/use-page-title";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import type { Member } from "@shared/schema";

type UnmatchedStatus = "pending" | "assigned" | "ignored";
type Filter = UnmatchedStatus | "all";

type UnmatchedRow = {
  id: number;
  userId: number;
  fromPhone: string;
  toPhone: string;
  body: string;
  status: UnmatchedStatus;
  resolvedMemberId: number | null;
  resolvedAt: string | null;
  createdAt: string;
};

type MemberOption = Member & { currentPropertyId?: number | null };

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: "pending", label: "Pending" },
  { key: "assigned", label: "Assigned" },
  { key: "ignored", label: "Ignored" },
  { key: "all", label: "All" },
];

function listKey(filter: Filter) {
  return [`/api/app/unmatched-sms?status=${filter}`] as const;
}

export default function AppMessagesUnmatchedSms() {
  usePageTitle("Unmatched texts");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [filter, setFilter] = useState<Filter>("pending");
  const [assignTarget, setAssignTarget] = useState<{ row: UnmatchedRow; mode: "assign" | "reassign" } | null>(null);
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);
  const [updatePhone, setUpdatePhone] = useState(true);

  const rowsQuery = useQuery<UnmatchedRow[]>({ queryKey: listKey(filter) });
  const membersQuery = useQuery<MemberOption[]>({ queryKey: ["/api/app/devices/members"], enabled: assignTarget != null });

  const invalidate = () => {
    for (const f of FILTERS) queryClient.invalidateQueries({ queryKey: listKey(f.key) });
    queryClient.invalidateQueries({ queryKey: ["/api/app/unmatched-sms/count"] });
  };

  const closeAssign = () => {
    setAssignTarget(null);
    setMemberSearch("");
    setSelectedMemberId(null);
    setUpdatePhone(true);
  };

  const assignMutation = useMutation({
    mutationFn: async (input: { id: number; mode: "assign" | "reassign"; memberId: number; updateMemberPhone: boolean }) => {
      const res = await apiRequest("POST", `/api/app/unmatched-sms/${input.id}/${input.mode}`, {
        memberId: input.memberId,
        updateMemberPhone: input.updateMemberPhone,
      });
      return (await res.json()) as UnmatchedRow & { memberId?: number };
    },
    onSuccess: (data, input) => {
      invalidate();
      closeAssign();
      toast({
        title: input.mode === "assign" ? "Text assigned" : "Text reassigned",
        description: "It now shows in the member's conversation.",
      });
      void data;
    },
    onError: (err: unknown) => {
      toast({ title: "Couldn't assign the text", description: parseErrorMessage(err, "Try again."), variant: "destructive" });
    },
  });

  const ignoreMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/app/unmatched-sms/${id}/ignore`);
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Text ignored" });
    },
    onError: (err: unknown) => {
      toast({ title: "Couldn't ignore the text", description: parseErrorMessage(err, "Try again."), variant: "destructive" });
    },
  });

  const reopenMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest("POST", `/api/app/unmatched-sms/${id}/reopen`);
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Text reopened" });
    },
    onError: (err: unknown) => {
      toast({ title: "Couldn't reopen the text", description: parseErrorMessage(err, "Try again."), variant: "destructive" });
    },
  });

  const filteredMembers = useMemo(() => {
    const q = String(memberSearch ?? "").trim().toLowerCase();
    const all = membersQuery.data ?? [];
    if (!q) return all.slice(0, 50);
    const digits = q.replace(/\D/g, "");
    return all
      .filter((m) => {
        const name = String(m.name ?? "").toLowerCase();
        const phone = String(m.phone ?? "").replace(/\D/g, "");
        return name.includes(q) || (digits.length >= 3 && phone.includes(digits));
      })
      .slice(0, 50);
  }, [memberSearch, membersQuery.data]);

  const rows = rowsQuery.data ?? [];

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <MessageSquareWarning className="h-5 w-5 text-brand" />
              <h1
                className="text-xl font-semibold text-gray-900"
                data-testid="text-unmatched-heading"
                data-tour="messages-unmatched-heading"
              >
                Unmatched texts
              </h1>
            </div>
            <p className="text-sm text-gray-500">
              Texts sent to your messaging number from phones that don't match a member. Assign each one to the right
              member so it lands in their conversation, or ignore it.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="messages-unmatched-sms" />
            <GuideButton pageKey="messages-unmatched-sms" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="messages-unmatched-sms" />

        <div className="mb-4 flex flex-wrap gap-2" data-tour="messages-unmatched-filter" data-testid="filter-unmatched-status">
          {FILTERS.map((f) => (
            <Button
              key={f.key}
              type="button"
              size="sm"
              variant={filter === f.key ? "default" : "outline"}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-unmatched-${f.key}`}
              aria-pressed={filter === f.key}
            >
              {f.label}
            </Button>
          ))}
        </div>

        <div className="space-y-3" data-tour="messages-unmatched-list" data-testid="list-unmatched-sms">
          {rowsQuery.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : rows.length === 0 ? (
            <Card className="p-8 text-center text-sm text-gray-600" data-testid="text-unmatched-empty">
              {filter === "pending" ? "Nothing to triage — every recent text matched a member." : "No texts in this view."}
            </Card>
          ) : (
            rows.map((row) => (
              <Card key={row.id} className="p-4" data-testid={`card-unmatched-${row.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium text-gray-900" data-testid={`text-unmatched-from-${row.id}`}>
                        {row.fromPhone}
                      </span>
                      <span className="text-gray-400">→</span>
                      <span className="text-gray-600">{row.toPhone}</span>
                      <Badge
                        variant={row.status === "pending" ? "default" : row.status === "assigned" ? "secondary" : "outline"}
                        data-testid={`badge-unmatched-status-${row.id}`}
                      >
                        {row.status}
                      </Badge>
                      <span className="text-xs text-gray-500">{format(new Date(row.createdAt), "PPp")}</span>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800" data-testid={`text-unmatched-body-${row.id}`}>
                      {row.body}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    {row.status === "pending" && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => setAssignTarget({ row, mode: "assign" })}
                          data-testid={`button-unmatched-assign-${row.id}`}
                        >
                          <UserPlus className="h-4 w-4 mr-1" />
                          Assign to member
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => ignoreMutation.mutate(row.id)}
                          disabled={ignoreMutation.isPending}
                          data-testid={`button-unmatched-ignore-${row.id}`}
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Ignore
                        </Button>
                      </>
                    )}
                    {row.status === "assigned" && (
                      <>
                        {row.resolvedMemberId != null && (
                          <Link
                            href={`/app/messages?memberId=${row.resolvedMemberId}&exact=1`}
                            className="inline-flex items-center gap-1 text-sm text-brand underline"
                            data-testid={`link-unmatched-conversation-${row.id}`}
                          >
                            Open conversation <ArrowRight className="h-3 w-3" />
                          </Link>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setAssignTarget({ row, mode: "reassign" })}
                          data-testid={`button-unmatched-reassign-${row.id}`}
                        >
                          Reassign
                        </Button>
                      </>
                    )}
                    {row.status === "ignored" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => reopenMutation.mutate(row.id)}
                        disabled={reopenMutation.isPending}
                        data-testid={`button-unmatched-reopen-${row.id}`}
                      >
                        <RotateCcw className="h-4 w-4 mr-1" />
                        Reopen
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        <div className="mt-6 text-sm text-gray-600">
          <Link href="/app/messages" className="text-brand underline inline-flex items-center gap-1" data-testid="link-back-to-messages">
            Back to Messages <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      <Dialog
        open={assignTarget != null}
        onOpenChange={(open) => {
          if (!open && assignMutation.isPending) return;
          if (!open) closeAssign();
        }}
      >
        <DialogContent
          className="max-h-[90vh] overflow-y-auto"
          onEscapeKeyDown={(e) => {
            if (assignMutation.isPending) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (assignMutation.isPending) e.preventDefault();
          }}
          onPointerDownOutside={(e) => {
            if (assignMutation.isPending) e.preventDefault();
          }}
          data-testid="dialog-unmatched-assign"
        >
          <DialogHeader>
            <DialogTitle>{assignTarget?.mode === "reassign" ? "Reassign text" : "Assign text to a member"}</DialogTitle>
            <DialogDescription>
              From {assignTarget?.row.fromPhone}. The text will appear in the chosen member's conversation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="unmatched-member-search" className="text-xs text-gray-600">
                Find a member
              </Label>
              <Input
                id="unmatched-member-search"
                placeholder="Name or phone"
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                data-testid="input-unmatched-member-search"
              />
            </div>
            <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200" role="radiogroup" aria-label="Member">
              {membersQuery.isLoading ? (
                <div className="p-3 text-sm text-gray-500">Loading members…</div>
              ) : filteredMembers.length === 0 ? (
                <div className="p-3 text-sm text-gray-500" data-testid="text-unmatched-no-members">
                  No members match.
                </div>
              ) : (
                filteredMembers.map((m) => {
                  const selected = m.id === selectedMemberId;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setSelectedMemberId(m.id)}
                      className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                        selected ? "bg-brand/5 text-gray-900" : "hover:bg-gray-50 text-gray-800"
                      }`}
                      data-testid={`radio-unmatched-member-${m.id}`}
                    >
                      <span className="font-medium">{m.name ?? `Member #${m.id}`}</span>
                      <span className="text-xs text-gray-500">{m.phone ?? "no phone"}</span>
                    </button>
                  );
                })
              )}
            </div>
            {assignTarget?.mode === "assign" && (
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <Checkbox
                  checked={updatePhone}
                  onCheckedChange={(v) => setUpdatePhone(v === true)}
                  data-testid="checkbox-unmatched-update-phone"
                />
                <span>Update the member's phone number to {assignTarget.row.fromPhone} so future texts match automatically.</span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeAssign} disabled={assignMutation.isPending} data-testid="button-cancel-unmatched-assign">
              Cancel
            </Button>
            <Button
              onClick={() =>
                assignTarget &&
                selectedMemberId != null &&
                assignMutation.mutate({
                  id: assignTarget.row.id,
                  mode: assignTarget.mode,
                  memberId: selectedMemberId,
                  updateMemberPhone: assignTarget.mode === "assign" ? updatePhone : false,
                })
              }
              disabled={selectedMemberId == null || assignMutation.isPending}
              data-testid="button-confirm-unmatched-assign"
            >
              {assignMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {assignTarget?.mode === "reassign" ? "Reassign" : "Assign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
